import React, { useCallback, useState, useMemo, useEffect } from 'react';
import * as PR from "../../../prime-modules/index";
import { useSelector, useDispatch } from 'react-redux';
import { getLanguageTranslates, uploadTranslations } from '../../../services/api';
import { editorConfig } from "../../../utils/reuse";
import QuillResizeImage from "quill-resize-image";
import Quill from "quill";
import "./AboutSection.scss";

Quill.register("modules/resize", QuillResizeImage);

const AboutSection = () => {
    const toast = React.useRef(null);
    const dispatch = useDispatch();
    const adminData = useSelector(state => state.adminAuth.adminSessionData);
    const headers = useMemo(() => {
        return { sessionid: adminData.sessionId };
    }, [adminData.sessionId]);

    const [aboutData, setAboutData] = useState({
        title: "",
        description: ""
    });
    const [loading, setLoading] = useState(true);
    const [dataLoaded, setDataLoaded] = useState(false);

    const modules = useMemo(() => editorConfig.modules, []);

    const fetchAboutSection = useCallback(() => {
        setLoading(true);
        const getResponse = (response) => {
            setLoading(false);
            if (response.result === 'SUCCESS') {
                const assetData = response.data;
                try {
                    const aboutSectionRaw = assetData?.cmsData?.aboutSection?.selectedLanguage;
                    if (aboutSectionRaw) {
                        const parsedData = typeof aboutSectionRaw === 'string' ? JSON.parse(aboutSectionRaw) : aboutSectionRaw;
                        setAboutData({
                            title: parsedData.title || "",
                            description: parsedData.description || ""
                        });
                    }
                    setDataLoaded(true);
                } catch (e) {
                    console.error("Error parsing aboutSection data", e);
                    setDataLoaded(true); // Still set to true to show empty fields
                }
            } else if (response.result === "FAILED" && response.error) {
                const error = response.error;
                toast.current.show({ severity: error.severity, summary: 'Error', detail: error.errorMsg || error.summary });
                setDataLoaded(true); // Still set to true to allow creating new
            }
        };
        getLanguageTranslates('en', headers, dispatch, getResponse);
    }, [dispatch, headers]);

    useEffect(() => {
        fetchAboutSection();
    }, [fetchAboutSection]);

    const handleSave = async () => {
        if (!aboutData.title.trim()) {
            toast.current.show({
                severity: "error",
                summary: "Validation Error",
                detail: "Title is a required field.",
            });
            return;
        }

        if (!String(aboutData.description || "").trim()) {
            toast.current.show({
                severity: "error",
                summary: "Validation Error",
                detail: "Description is a required field.",
            });
            return;
        }

        const request = {
            "cmsData": {
                "aboutSection": JSON.stringify(aboutData)
            }
        };

        setLoading(true);
        const getCMSResponse = (response) => {
            setLoading(false);
            if (response.result === 'SUCCESS') {
                toast.current.show({ severity: 'success', summary: 'Success', detail: 'Updated successfully' });
                fetchAboutSection();
            } else if (response.result === "FAILED" && response.error) {
                const error = response.error;
                toast.current.show({ severity: error.severity, summary: 'Error', detail: error.errorMsg || error.summary });
            }
        };
        uploadTranslations('en', request, headers, dispatch, getCMSResponse);
    };

    return (
        <div className='general about-section-container'>
            <div className="card">
                {loading && (
                    <div className="progress-spinner">
                        <PR.ProgressSpinner
                            className="progressSpinner"
                            animationDuration=".5s"
                        />
                    </div>
                )}
                {dataLoaded && (
                    <>
                        <div className='paragraph-tag mb-4'>
                        </div>
                        <div className="grid input-field align-items-center mb-3">
                            <div className="col-12">
                                <label className="block mb-2">Title</label>
                                <PR.InputText
                                    value={aboutData.title}
                                    onChange={(e) => setAboutData(prev => ({ ...prev, title: e.target.value }))}
                                    placeholder="Enter title"
                                />
                            </div>
                        </div>
                        <div className="grid align-items-center mb-3">
                            <div className="col-12">
                                <label className="block mb-2">Description</label>
                                <PR.Editor
                                    value={aboutData.description}
                                    onTextChange={(e) => setAboutData(prev => ({ ...prev, description: e.htmlValue }))}
                                    style={{ height: "450px" }}
                                    placeholder="Enter description"
                                    modules={modules}
                                />
                            </div>
                        </div>
                        <div className="button-container mt-3">
                            <PR.Button
                                label="Save"
                                onClick={handleSave}
                                className="save-button"
                                disabled={loading}
                            />
                        </div>
                    </>
                )}
            </div>
            <PR.Toast ref={toast} position="top-right" />
        </div>
    );
};

export default AboutSection;
